import { useRef, useState } from 'react'
import type { List } from 'app/generated/graphql'
import { Button } from 'app/components/ui/button'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useSettings } from 'app/hooks/useSettings'
import { notifyError } from 'app/services/notify.client'
import { useListStore } from 'app/stores/StoreContext'
import type { SystemListDef } from 'app/types/navigation'
import { useWorkspace } from 'app/utils'
import { logError } from 'lib/logging'
import { stagesInput } from 'lib/practiceLists'
import { hasPracticeStage, setupSkipSettingKey } from 'lib/systemListBoard'

export function SystemListSetupCard({
  def,
  list,
  onBuildOwn,
  onChanged,
}: {
  def: SystemListDef
  list: List
  onBuildOwn: () => void
  onChanged: () => void
}) {
  const listStore = useListStore()
  const checkPermission = useCheckPermission()
  const workspace = useWorkspace()
  const skipKey = setupSkipSettingKey(def, workspace?.id ?? ``)
  const [skipped, saveSkipped, skipLoaded, skipReadFailed] = useSettings<boolean>(skipKey)
  const [saving, setSaving] = useState(false)
  // `disabled` lands a render late, so a fast second click would send the template twice.
  const savingNow = useRef(false)
  const template = def.setupTemplate

  const canEdit = checkPermission([`org:pages:pipelines`, `pages:pipelines`])
  // A failed read must not show the card to someone who already skipped it.
  if (!template || !canEdit || hasPracticeStage(list) || !skipLoaded || skipReadFailed || skipped) return null

  const applyTemplate = async () => {
    if (savingNow.current) return
    savingNow.current = true
    setSaving(true)
    const stages = [...stagesInput(list.stages ?? []), ...template.map(({ name, color }) => ({ name, color }))]
    const result = await listStore.update(list.id, { stages })
    savingNow.current = false
    setSaving(false)
    if (result.error) {
      logError(`Error adding a system list's template stages`, result.error)
      notifyError(`Couldn't add those stages. Please try again.`)
      return
    }
    onChanged()
  }

  return (
    <section className="mt-5 flex flex-col gap-3 rounded-lg border border-default bg-surface-raised px-4 py-3">
      <div>
        <h2 className="text-ui-sm font-semibold text-strong">Set up your stages</h2>
        <p className="text-body-sm text-subtle">
          Hygiene Recall moves people between Due and Scheduled on its own. Add your own stages for the steps in
          between, like who you have reached and who replied.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={saving} onClick={applyTemplate}>
          {`Use ${template.map((stage) => stage.name).join(` and `)}`}
        </Button>
        <Button size="sm" variant="secondary" onClick={onBuildOwn}>
          Build my own
        </Button>
        <Button size="sm" variant="tertiary" onClick={() => saveSkipped(skipKey, true)}>
          Skip
        </Button>
      </div>
    </section>
  )
}
